import React from 'react';
import Button from '../../Components/Button/Button';

export function AdminBadge({ variant = 'muted', children }) {
  return <span className={`admin-badge admin-badge--${variant}`}>{children}</span>;
}

export function AdminSearch({ value, onChange, placeholder = 'Search...', fullWidth = false }) {
  return (
    <div
      className="properties-search-wrapper"
      style={fullWidth ? { maxWidth: '100%', width: '100%' } : { maxWidth: '320px' }}
    >
      <img
        src={window.getPublicUrl('/assets/images/SearchIcon.svg')}
        alt=""
        className="properties-search-icon"
      />
      <input
        type="text"
        className="properties-search-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function AdminStatCard({ label, value, icon, subtitle }) {
  const isIconPath =
    typeof icon === 'string' &&
    (icon.startsWith('/') || icon.includes('.svg') || icon.includes('.png'));

  return (
    <div className="dashboard-stat-card">
      <div className="dashboard-stat-header">
        <div className="dashboard-stat-icon">
          {isIconPath ? (
            <img
              src={window.getPublicUrl(icon)}
              alt={label}
              className="dashboard-stat-icon-img"
            />
          ) : (
            icon
          )}
        </div>
        <div className="dashboard-stat-label">{label}</div>
      </div>
      <div className="dashboard-stat-value">{value}</div>
      {subtitle && (
        <p style={{ margin: 0, fontSize: '12px', color: '#7c7e93' }}>{subtitle}</p>
      )}
    </div>
  );
}

export function statusToBadge(status) {
  const s = String(status || '').toLowerCase();
  if (['active', 'completed', 'passed', 'success', 'enterprise', 'premium'].includes(s)) {
    return 'success';
  }
  if (['pending', 'in-progress', 'warning', 'standard'].includes(s)) {
    return 'warning';
  }
  if (['inactive', 'failed', 'destructive', 'disabled', 'refund'].includes(s) || s.includes('refund')) {
    return 'danger';
  }
  if (s.includes('rent')) {
    return 'primary';
  }
  if (s.includes('screening') || s.includes('subscription')) {
    return 'success';
  }
  if (['basic', 'secondary'].includes(s)) {
    return 'muted';
  }
  return 'primary';
}

export function AdminModal({ open, onClose, title, description, children, footer }) {
  if (!open) return null;
  return (
    <div className="admin-modal-overlay" onClick={onClose} role="presentation">
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} role="dialog">
        {title && <h3>{title}</h3>}
        {description && <p>{description}</p>}
        {children}
        {footer}
      </div>
    </div>
  );
}

export function AdminModalFooter({
  onCancel,
  onConfirm,
  confirmLabel = 'Save',
  cancelLabel = 'Cancel',
  confirmVariant = 'success',
  danger,
  confirmDisabled = false,
  cancelDisabled = false,
}) {
  return (
    <div className="admin-modal-actions">
      <Button variant="outline" onClick={onCancel} disabled={cancelDisabled}>
        {cancelLabel}
      </Button>
      <Button
        variant={danger ? 'danger' : confirmVariant}
        onClick={onConfirm}
        disabled={confirmDisabled}
      >
        {confirmLabel}
      </Button>
    </div>
  );
}

export function getPaginationRange(currentPage, totalPages) {
  if (totalPages <= 0) return [];

  const delta = 1;
  const range = [];
  const rangeWithDots = [];
  let previous;

  for (let i = 1; i <= totalPages; i += 1) {
    if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
      range.push(i);
    }
  }

  for (const page of range) {
    if (previous) {
      if (page - previous === 2) {
        rangeWithDots.push(previous + 1);
      } else if (page - previous !== 1) {
        rangeWithDots.push('ellipsis');
      }
    }
    rangeWithDots.push(page);
    previous = page;
  }

  return rangeWithDots;
}

export function AdminPagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
}) {
  const showingFrom = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo = Math.min(currentPage * pageSize, totalItems);
  const pages = getPaginationRange(currentPage, totalPages);

  return (
    <div className="admin-table-footer">
      <span className="admin-pagination-info">
        Showing {showingFrom}–{showingTo} of {totalItems}
      </span>
      <div className="admin-pagination-actions">
        <Button
          size="small"
          variant="outline"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          Previous
        </Button>
        <div className="admin-pagination-numbers" role="navigation" aria-label="Pagination">
          {pages.map((page, index) =>
            page === 'ellipsis' ? (
              <span
                key={`ellipsis-${index}`}
                className="admin-pagination-num admin-pagination-num--ellipsis"
                aria-hidden="true"
              >
                …
              </span>
            ) : (
              <button
                key={page}
                type="button"
                className={`admin-pagination-num${page === currentPage ? ' admin-pagination-num--active' : ''}`}
                onClick={() => onPageChange(page)}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </button>
            )
          )}
        </div>
        <Button
          size="small"
          variant="outline"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
